import { Position } from '@xyflow/react';
import type { WorkflowDto, WorkflowStepDto } from '../workflow';
import { SPECIAL_ACTIONS, isAnchorAction } from '../specialActions';
import { assignYOrder, computeLevels } from './layout';
import type { FlattenedStep, WorkflowEdge, WorkflowGraph, WorkflowNode } from './types';

const collectSteps = (
  steps: Record<string, WorkflowStepDto>,
  parentStepId: string | undefined,
  acc: FlattenedStep[],
): void => {
  Object.values(steps).forEach((step) => {
    const entry: FlattenedStep = { step, parentStepId };
    acc.push(entry);

    if (step.nestedSteps) {
      collectSteps(step.nestedSteps, step.id, acc);
    }
  });
};

/**
 * Map a normalised WorkflowDto into a xyflow graph.
 *
 * Uses a left-to-right layered layout based on the control-flow
 * dependencies (runAfter). Steps without predecessors sit in the first
 * column; their dependents appear to the right. Within each column,
 * steps are ordered using a barycenter heuristic (see ./layout.ts) to
 * minimise edge crossings.
 *
 * Nested steps (e.g. inside a forEach) render as xyflow child nodes
 * inside their parent container using `parentId`. The parent container
 * is sized to fit the inner sub-layout so the nested steps are visually
 * grouped.
 */
export const workflowToXyflow = (workflow: WorkflowDto): WorkflowGraph => {
  const nodes: WorkflowNode[] = [];
  const edges: WorkflowEdge[] = [];

  const flattened: FlattenedStep[] = [];
  collectSteps(workflow.steps, undefined, flattened);

  if (flattened.length === 0) {
    return { nodes, edges };
  }

  const xSpacing = 250;
  const ySpacing = 120;
  // Padding inside a forEach container and space for the header label.
  const innerPaddingX = 20;
  const innerPaddingY = 20;
  const innerHeaderHeight = 36;
  // Default node dimensions for sizing estimates.
  const defaultNodeWidth = 200;
  const defaultNodeHeight = 60;

  const byId = new Map<string, FlattenedStep>();
  flattened.forEach((entry) => {
    byId.set(entry.step.id, entry);
  });

  // Separate root steps from directly-nested steps (one level deep).
  const rootFlattened = flattened.filter((f) => !f.parentStepId);
  const nestedByParent = new Map<string, FlattenedStep[]>();
  flattened.forEach((f) => {
    const parentId = f.parentStepId;
    if (!parentId) return;
    const list = nestedByParent.get(parentId) ?? [];
    list.push(f);
    nestedByParent.set(parentId, list);
  });

  // Pre-compute the visual size each forEach container needs to hold its children.
  const containerSizes = new Map<string, { width: number; height: number }>();

  nestedByParent.forEach((innerEntries, parentId) => {
    const innerLevels = computeLevels(innerEntries);
    const maxLevel = Math.max(0, ...innerLevels.values());

    const countByLevel = new Map<number, number>();
    innerLevels.forEach((l) => {
      countByLevel.set(l, (countByLevel.get(l) ?? 0) + 1);
    });
    const maxCount = Math.max(1, ...countByLevel.values());

    const width = innerPaddingX * 2 + (maxLevel + 1) * xSpacing - (xSpacing - defaultNodeWidth);
    const height =
      innerHeaderHeight + innerPaddingY * 2 + maxCount * ySpacing - (ySpacing - defaultNodeHeight);

    containerSizes.set(parentId, { width, height });
  });

  // Lay out root steps using a topological level computed from root-only edges.
  const rootLevels = computeLevels(rootFlattened);
  const rootByLevel = assignYOrder(rootFlattened, rootLevels);

  // Compute the x position of each column, accounting for forEach containers
  // that may be wider than a regular node.
  const columnX = new Map<number, number>();
  let currentX = 0;
  Array.from(rootByLevel.keys())
    .sort((a, b) => a - b)
    .forEach((l) => {
      columnX.set(l, currentX);
      const idsInLevel = rootByLevel.get(l) ?? [];
      let maxWidth = defaultNodeWidth;
      idsInLevel.forEach((id) => {
        const cs = containerSizes.get(id);
        if (cs) {
          maxWidth = Math.max(maxWidth, cs.width);
        }
      });
      currentX += maxWidth + 50;
    });

  // Emit root nodes.
  Array.from(rootByLevel.keys())
    .sort((a, b) => a - b)
    .forEach((l) => {
      const idsInLevel = rootByLevel.get(l) ?? [];

      idsInLevel.forEach((stepId, index) => {
        const entry = byId.get(stepId);
        if (!entry) {
          return;
        }

        const { step, parentStepId } = entry;
        const x = columnX.get(l) ?? l * xSpacing;
        const position = { x, y: index * ySpacing };
        const cs = containerSizes.get(stepId);

        nodes.push({
          id: step.id,
          type: 'workflowNode',
          position,
          ...(cs && { style: { width: cs.width, height: cs.height } }),
          data: {
            stepId: step.id,
            actionId: step.actionId,
            input: step.input,
            settings: step.settings,
            label: step.actionId,
            parentStepId,
          },
          sourcePosition: Position.Right,
          targetPosition: Position.Left,
          ...(isAnchorAction(step.actionId) && { deletable: false }),
        });
      });
    });

  // Emit nested nodes as xyflow children of their parent container.
  nestedByParent.forEach((innerEntries, parentId) => {
    const innerLevels = computeLevels(innerEntries);
    const innerByLevel = assignYOrder(innerEntries, innerLevels);

    Array.from(innerByLevel.keys())
      .sort((a, b) => a - b)
      .forEach((l) => {
        const idsInLevel = innerByLevel.get(l) ?? [];

        idsInLevel.forEach((stepId, index) => {
          const entry = byId.get(stepId);
          if (!entry) {
            return;
          }

          const { step } = entry;
          const position = {
            x: innerPaddingX + l * xSpacing,
            y: innerHeaderHeight + innerPaddingY + index * ySpacing,
          };

          nodes.push({
            id: step.id,
            type: 'workflowNode',
            position,
            parentId,
            extent: 'parent',
            data: {
              stepId: step.id,
              actionId: step.actionId,
              input: step.input,
              settings: step.settings,
              label: step.actionId,
              parentStepId: parentId,
            },
            sourcePosition: Position.Right,
            targetPosition: Position.Left,
            ...(isAnchorAction(step.actionId) && { deletable: false }),
          });
        });
      });
  });

  // Control-flow edges from runAfter.
  flattened.forEach(({ step }) => {
    step.runAfter.forEach((runAfter) => {
      const fromId = runAfter.stepId;
      if (!fromId || !byId.has(fromId)) {
        return;
      }

      const fromEntry = byId.get(fromId);
      if (!fromEntry) return;
      const isRoute = fromEntry.step.actionId === SPECIAL_ACTIONS.route;

      const edgeId = `control:${fromId}->${step.id}`;
      const isConditional = runAfter.enableOnCondition === true;
      let edgeStyle: WorkflowEdge['style'];
      if (isRoute) {
        edgeStyle = { strokeDasharray: '4 2', strokeWidth: 1.5, stroke: '#b36b00' };
      } else if (isConditional) {
        edgeStyle = { strokeDasharray: '5 5', strokeWidth: 1.5 };
      } else {
        edgeStyle = { strokeWidth: 1.5 };
      }
      edges.push({
        id: edgeId,
        source: fromId,
        target: step.id,
        // Anchor control edges to the generic `out`/`in` handles so they
        // resolve in both Flow and Dataflow modes (where per-field handles
        // would otherwise be the only matches on contract-bearing nodes).
        sourceHandle: 'out',
        targetHandle: 'in',
        label: runAfter.routeKey && isRoute ? runAfter.routeKey : undefined,
        style: edgeStyle,
        interactionWidth: 20,
        data: {
          kind: 'control',
          enableOnCondition: runAfter.enableOnCondition,
          routeKey: runAfter.routeKey,
        },
      });
    });
  });

  // Data-flow edges from StepOutput input bindings. Visual only; ignored
  // when converting the graph back to a WorkflowDto.
  flattened.forEach(({ step }) => {
    Object.entries(step.input).forEach(([inputName, binding]) => {
      if (!binding || binding.kind !== 'StepOutput') {
        return;
      }

      const stepBinding = binding;
      const fromId = stepBinding.stepId;
      if (!fromId || !byId.has(fromId)) {
        return;
      }

      const edgeId = `data:${fromId}->${step.id}:${inputName}`;
      edges.push({
        id: edgeId,
        source: fromId,
        target: step.id,
        sourceHandle: `out:${stepBinding.target}`,
        targetHandle: `in:${inputName}`,
        label: inputName,
        data: { kind: 'data' },
      });
    });
  });

  return { nodes, edges };
};
